import React, { useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { MdPhotoCamera, MdCloudUpload } from "react-icons/md";
import { storeSettingsService, getUploadedImageUrl } from "../../../services/storeSettings.service";
import { AWS_URL } from "../../../services/http.service";
import { useCraftStore } from "./CraftStoreContext";

const MAX_SIZE_MB = 5;

export const resolveImageUrl = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  return `${AWS_URL}${raw}`;
};

// Returns { inputProps, openPicker, uploading } for a hidden file input
export const useImageUpload = (onUploaded) => {
  const { bussinessUuid } = useCraftStore();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`Image must be smaller than ${MAX_SIZE_MB}MB`);
        return;
      }
      try {
        setUploading(true);
        const res = await storeSettingsService.uploadBanner(bussinessUuid, file);
        const url = getUploadedImageUrl(res);
        if (!url) throw new Error("Image URL missing from upload response");
        onUploaded(url.trim());
        toast.success("Image uploaded. Save your layout to keep it.");
      } catch (err) {
        console.error("Image upload error:", err);
        toast.error(
          err?.response?.data?.error || err.message || "Image upload failed",
        );
      } finally {
        setUploading(false);
      }
    },
    [bussinessUuid, onUploaded],
  );

  return {
    uploading,
    openPicker: () => inputRef.current?.click(),
    input: (
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
      />
    ),
  };
};

// Floating "Change image" button shown on the canvas in edit mode
export const ImageChangeButton = ({
  onUploaded,
  enabled,
  label = "Change image",
  className = "top-3 right-3",
}) => {
  const { openPicker, uploading, input } = useImageUpload(onUploaded);
  if (!enabled) return null;
  return (
    <>
      {input}
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          openPicker();
        }}
        disabled={uploading}
        className={`absolute z-20 ${className} flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-black/70 hover:bg-black/85 text-white text-[11px] font-bold shadow-md backdrop-blur-sm disabled:opacity-60`}
      >
        <MdPhotoCamera size={14} />
        {uploading ? "Uploading..." : label}
      </button>
    </>
  );
};

// Upload button + URL field for the settings panel
export const ImageUploadField = ({ label, value, onChange }) => {
  const { openPicker, uploading, input } = useImageUpload(onChange);
  const preview = resolveImageUrl(value);
  return (
    <div className="space-y-2">
      <span className="text-[11px] text-gray-500 block">{label}</span>
      {preview && (
        <img
          src={preview}
          alt="Preview"
          className="w-full h-28 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
        />
      )}
      {input}
      <button
        type="button"
        onClick={openPicker}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-indigo-300 text-indigo-600 dark:text-indigo-400 text-xs font-bold hover:bg-indigo-50 dark:hover:bg-indigo-950/40 disabled:opacity-60"
      >
        <MdCloudUpload size={16} />
        {uploading ? "Uploading..." : "Upload image"}
      </button>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
};
